import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Globe, Loader2, RefreshCw } from 'lucide-react';
import { api } from '@/services/client';
import { tabService } from '@/services/tabs';
import { useTabMonitorSession } from '@/context/TabMonitorSessionContext';
import { isPersonalOrSensitiveUrl } from '@/utils/personalTabUrls';
import { Button } from '@/components/ui/Button';
import type { ApiBrowserTab } from '@/types';
import type { CollabAnnotation } from '@/services/collab-session-service';
import { buildAnnotatedSegments, captureTextSelection } from './collabDocumentUtils';
import type { CollabSharedDocument, CollabTextSelection } from './collabTypes';
import { getAnnotationNote, getSelectedQuote } from './collabTypes';

interface CollabDocumentPanelProps {
  annotations: CollabAnnotation[];
  onSelectionChange: (selection: CollabTextSelection | null) => void;
  onAnnotationClick: (annotationId: string) => void;
  onDocumentChange?: (document: CollabSharedDocument | null) => void;
}

async function loadDocumentForTab(tab: ApiBrowserTab): Promise<CollabSharedDocument> {
  const title = tab.title || 'Untitled page';
  const url = tab.url || '';

  try {
    const analyses = await tabService.getTabAnalyses(tab.id, 1);
    const latest = analyses[0];
    if (latest?.summary?.trim()) {
      return {
        tabId: tab.id,
        title,
        url,
        body: latest.summary.trim(),
        source: 'analysis',
      };
    }
  } catch {
    /* fall through */
  }

  if (url) {
    try {
      const { data } = await api.post<Record<string, unknown>>(
        '/content/extract',
        { url },
        { timeout: 120000 },
      );
      const content = String(data.content ?? data.text ?? '').trim();
      if (content) {
        return {
          tabId: tab.id,
          title: String(data.title ?? title),
          url,
          body: content,
          source: 'extract',
        };
      }
    } catch {
      /* fall through */
    }
  }

  return {
    tabId: tab.id,
    title,
    url,
    body: [
      title,
      url ? `Source: ${url}` : '',
      '',
      'No extracted page content is available yet for this tab.',
      'Open the page in your browser extension so Khiimori can capture it, then click Refresh.',
    ].filter(Boolean).join('\n\n'),
    source: 'summary',
  };
}

export function CollabDocumentPanel({
  annotations,
  onSelectionChange,
  onAnnotationClick,
  onDocumentChange,
}: CollabDocumentPanelProps) {
  const { workspaceSessionId } = useTabMonitorSession();
  const bodyRef = useRef<HTMLDivElement>(null);

  const [tabs, setTabs] = useState<ApiBrowserTab[]>([]);
  const [selectedTabId, setSelectedTabId] = useState<string>('');
  const [pageDocument, setPageDocument] = useState<CollabSharedDocument | null>(null);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadTabs = useCallback(async () => {
    if (!workspaceSessionId) {
      setTabs([]);
      setSelectedTabId('');
      setPageDocument(null);
      return;
    }
    setLoadingTabs(true);
    setLoadError(null);
    try {
      const { tabs: raw } = await tabService.getTabsBySession(workspaceSessionId, 1, 100);
      const eligible = raw.filter(
        (t) => t.url && !isPersonalOrSensitiveUrl(t.url) && t.status !== 'closed',
      );
      setTabs(eligible);
      setSelectedTabId((prev) => {
        if (prev && eligible.some((t) => t.id === prev)) return prev;
        const active = eligible.find((t) => t.status === 'active');
        return active?.id ?? eligible[0]?.id ?? '';
      });
    } catch (err) {
      console.error('Failed to load workspace tabs:', err);
      setLoadError('Could not load workspace tabs.');
      setTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  }, [workspaceSessionId]);

  const loadDocument = useCallback(async (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) {
      setPageDocument(null);
      return;
    }
    setLoadingDoc(true);
    setLoadError(null);
    try {
      const doc = await loadDocumentForTab(tab);
      setPageDocument(doc);
    } catch (err) {
      console.error('Failed to load tab content:', err);
      setLoadError('Failed to load page content for this tab.');
    } finally {
      setLoadingDoc(false);
    }
  }, [tabs]);

  useEffect(() => {
    loadTabs();
  }, [loadTabs]);

  useEffect(() => {
    if (selectedTabId) {
      loadDocument(selectedTabId);
    }
  }, [selectedTabId, loadDocument]);

  useEffect(() => {
    onDocumentChange?.(pageDocument);
  }, [pageDocument, onDocumentChange]);

  useEffect(() => {
    const onSelectionChangeEvent = () => {
      onSelectionChange(captureTextSelection(bodyRef.current));
    };
    window.document.addEventListener('selectionchange', onSelectionChangeEvent);
    return () => window.document.removeEventListener('selectionchange', onSelectionChangeEvent);
  }, [onSelectionChange, pageDocument?.body]);

  const docAnnotations = pageDocument
    ? annotations.filter((ann) => {
        const tabId = (ann.position as { tabId?: string } | undefined)?.tabId;
        return !tabId || tabId === pageDocument.tabId;
      })
    : [];

  const segments = pageDocument ? buildAnnotatedSegments(pageDocument.body, docAnnotations) : [];

  return (
    <div className="glass-card shadow-2xl overflow-hidden max-w-4xl mx-auto">
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-khiimori-text-primary text-lg font-semibold truncate">
              {pageDocument?.title ?? 'Shared page'}
            </h2>
            {pageDocument?.url && (
              <a
                href={pageDocument.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-200 text-sm mt-1 inline-flex items-center gap-1 hover:text-white truncate max-w-full"
              >
                <Globe className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{pageDocument.url}</span>
                <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {tabs.length > 0 && (
              <select
                value={selectedTabId}
                onChange={(e) => setSelectedTabId(e.target.value)}
                className="text-sm rounded-lg bg-khiimori-bg-elevated/20 border border-white/20 text-white px-2 py-1.5 max-w-[200px]"
                aria-label="Select tab to share"
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id} className="text-khiimori-text-primary">
                    {tab.title || tab.url}
                  </option>
                ))}
              </select>
            )}
            <Button
              onClick={() => {
                loadTabs();
                if (selectedTabId) loadDocument(selectedTabId);
              }}
              variant="ghost"
              size="sm"
              aria-label="Refresh content"
              title="Refresh content"
            >
              <RefreshCw className={`w-4 h-4 text-white ${loadingTabs || loadingDoc ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {pageDocument && (
          <p className="text-violet-200/80 text-xs mt-2">
            Select text below, then use Highlight, Comment, Bookmark, or Tag — everyone in the session sees the exact quote and your note.
          </p>
        )}
      </div>

      <div className="p-6 lg:p-8">
        {!workspaceSessionId && (
          <p className="text-khiimori-text-muted text-sm text-center py-8">
            Open the Tabs workspace to load a real page from your tracked tabs.
          </p>
        )}

        {workspaceSessionId && loadingTabs && !pageDocument && (
          <div className="flex items-center justify-center gap-2 py-12 text-khiimori-text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading workspace tabs...</span>
          </div>
        )}

        {workspaceSessionId && !loadingTabs && tabs.length === 0 && (
          <p className="text-khiimori-text-muted text-sm text-center py-8">
            No shareable tabs in your workspace yet. Browse with the Khiimori extension to add tabs here.
          </p>
        )}

        {loadError && (
          <p className="text-error-400 text-sm mb-4">{loadError}</p>
        )}

        {loadingDoc && (
          <div className="flex items-center justify-center gap-2 py-8 text-khiimori-text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading page content...</span>
          </div>
        )}

        {pageDocument && !loadingDoc && (
          <div
            ref={bodyRef}
            className="prose prose-invert max-w-none text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap select-text"
          >
            {segments.map((segment, idx) => {
              if (!segment.annotation) {
                return <span key={`plain-${idx}`}>{segment.text}</span>;
              }
              const ann = segment.annotation;
              const note = getAnnotationNote(ann);
              return (
                <mark
                  key={ann.id}
                  className="cursor-pointer rounded px-0.5 transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: `${ann.color}55`,
                    borderBottom: `2px solid ${ann.color}`,
                  }}
                  title={note || getSelectedQuote(ann) || ann.type}
                  onClick={() => onAnnotationClick(ann.id)}
                >
                  {segment.text}
                </mark>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
